import { create } from 'zustand'
import type {
  OrgDeletionRequest,
  OrgExportJob,
  ExportSizeEstimate,
  ActiveSessions,
} from '@/lib/schemas'
import {
  ApiRequestError,
  scheduleOrgDeletion,
  cancelOrgDeletion,
  getDeletionStatus,
  getActiveSessions,
  getExportSizeEstimate,
  startOrgExport,
  listOrgExports,
  getOrgExport,
} from '@/lib/api'

interface OrgLifecycleState {
  // Deletion
  deletionRequest: OrgDeletionRequest | null
  activeSessions: ActiveSessions | null

  // Export
  sizeEstimate: ExportSizeEstimate | null
  currentExport: OrgExportJob | null
  recentExports: OrgExportJob[]
  pollTimerId: ReturnType<typeof setTimeout> | null

  // Shared
  isLoading: boolean
  error: string | null
}

interface OrgLifecycleActions {
  loadStatus: () => Promise<void>
  loadActiveSessions: () => Promise<void>
  loadSizeEstimate: () => Promise<void>
  loadExports: () => Promise<void>

  scheduleDeletion: (confirmOrgName: string, expectedName: string) => Promise<void>
  cancelDeletion: () => Promise<void>

  startExport: () => Promise<void>
  pollCurrentExport: () => Promise<void>
  stopPolling: () => void
}

type OrgLifecycleStore = OrgLifecycleState & OrgLifecycleActions

const initialState: OrgLifecycleState = {
  deletionRequest: null,
  activeSessions: null,
  sizeEstimate: null,
  currentExport: null,
  recentExports: [],
  pollTimerId: null,
  isLoading: false,
  error: null,
}

function extractError(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export const useOrgLifecycleStore = create<OrgLifecycleStore>((set, get) => ({
  ...initialState,

  loadStatus: async () => {
    set({ isLoading: true, error: null })
    try {
      const status = await getDeletionStatus()
      set({
        deletionRequest: status.status === 'pending' ? status.request : null,
        isLoading: false,
      })
    } catch (err) {
      set({ isLoading: false, error: extractError(err) })
    }
  },

  loadActiveSessions: async () => {
    try {
      const sessions = await getActiveSessions()
      set({ activeSessions: sessions })
    } catch (err) {
      // Non-fatal — modal can still render without the active-session warning
      console.error('Failed to load active sessions', err)
    }
  },

  loadSizeEstimate: async () => {
    try {
      const estimate = await getExportSizeEstimate()
      set({ sizeEstimate: estimate })
    } catch (err) {
      set({ error: extractError(err) })
    }
  },

  loadExports: async () => {
    try {
      const { items } = await listOrgExports()
      set({ recentExports: items })
      // Check if there's an active export we should be polling
      const active = items.find((j) => j.status === 'pending' || j.status === 'running')
      if (active) {
        set({ currentExport: active })
        // Start polling if not already
        if (!get().pollTimerId) {
          void get().pollCurrentExport()
        }
      } else {
        set({ currentExport: null })
      }
    } catch (err) {
      set({ error: extractError(err) })
    }
  },

  scheduleDeletion: async (confirmOrgName: string, expectedName: string) => {
    if (confirmOrgName !== expectedName) {
      set({ error: 'Organisation name does not match' })
      return
    }
    set({ isLoading: true, error: null })
    try {
      const request = await scheduleOrgDeletion()
      set({ deletionRequest: request, isLoading: false })
    } catch (err) {
      set({ isLoading: false, error: extractError(err) })
      throw err
    }
  },

  cancelDeletion: async () => {
    set({ isLoading: true, error: null })
    try {
      await cancelOrgDeletion()
      set({ deletionRequest: null, isLoading: false })
    } catch (err) {
      set({ isLoading: false, error: extractError(err) })
      throw err
    }
  },

  startExport: async () => {
    set({ isLoading: true, error: null })
    try {
      await startOrgExport()
      set({ isLoading: false })
      // Refresh state: size estimate may change, and we want to show the new job
      await get().loadExports()
      await get().loadSizeEstimate()
    } catch (err) {
      set({ isLoading: false, error: extractError(err) })
      throw err
    }
  },

  pollCurrentExport: async () => {
    const { currentExport } = get()
    if (!currentExport) return

    try {
      const updated = await getOrgExport(currentExport.id)
      set({ currentExport: updated })

      if (updated.status === 'completed' || updated.status === 'failed') {
        // Terminal state — stop polling and refresh full list
        set({ pollTimerId: null })
        await get().loadExports()
        return
      }

      // Keep polling every 5 seconds
      const timerId = setTimeout(() => {
        void get().pollCurrentExport()
      }, 5000)
      set({ pollTimerId: timerId })
    } catch (err) {
      console.error('Export polling failed', err)
      set({ pollTimerId: null })
    }
  },

  stopPolling: () => {
    const { pollTimerId } = get()
    if (pollTimerId) {
      clearTimeout(pollTimerId)
      set({ pollTimerId: null })
    }
  },
}))
